import { useState, useEffect } from "react";
import api from "../lib/api";
import { acquireLock, releaseLock } from "../lib/locks";
import { useConfirm } from "../contexts/ConfirmContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { RowMenu } from "../components/RowMenu";
import { Plus, Search, Edit, Trash2, Users, Phone, Mail, BookOpen, Clock } from "lucide-react";
import { useSettings } from "../contexts/SettingsContext";
import { Link, useNavigate } from "react-router-dom";
import { AttachmentsButton } from "../components/AttachmentsButton";

const CustomersPage = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { settings } = useSettings();
  const { can } = useAuth();
  const canDelete = can("can_delete");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    payment_terms_days: "",
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get("/customers");
      setCustomers(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, payment_terms_days: formData.payment_terms_days === "" ? null : parseInt(formData.payment_terms_days, 10) };
    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, payload);
        toast.success("Client mis à jour");
      } else {
        await api.post("/customers", payload);
        toast.success("Client créé");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = async (customer) => {
    const lock = await acquireLock("customer", customer.id);
    if (!lock.ok) { toast.error(lock.message); return; }
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      payment_terms_days: customer.payment_terms_days ?? "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ description: "Êtes-vous sûr de vouloir supprimer ce client ?" }))) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Client supprimé");
      fetchCustomers();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    if (editingCustomer) releaseLock("customer", editingCustomer.id);
    setEditingCustomer(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      payment_terms_days: "",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const filteredCustomers = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.phone && c.phone.includes(searchTerm))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-[Chivo] tracking-tight text-slate-900">
            Clients
          </h1>
          <p className="text-slate-500 mt-1">Gérez votre base de clients</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-customer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau client
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-[Chivo]">
                {editingCustomer ? "Modifier le client" : "Nouveau client"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="overline">Nom complet</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-sm"
                  data-testid="input-customer-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="overline">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="rounded-sm"
                    data-testid="input-customer-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="overline">Téléphone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="rounded-sm"
                    data-testid="input-customer-phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="overline">Adresse</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="rounded-sm"
                  data-testid="input-customer-address"
                />
              </div>
              <div className="space-y-2">
                <Label className="overline">Délai de paiement accordé (jours)</Label>
                <Input
                  type="number"
                  min="0"
                  max="365"
                  value={formData.payment_terms_days}
                  onChange={(e) => setFormData({ ...formData, payment_terms_days: e.target.value })}
                  className="rounded-sm font-mono"
                  placeholder={`Vide = délai global (${settings.overdue_days || 7} j)`}
                  data-testid="input-customer-terms"
                />
                <p className="text-xs text-slate-500">Ce client sera considéré en retard après ce nombre de jours, à la place du délai global.</p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="rounded-sm"
                >
                  Annuler
                </Button>
                <Button type="submit" className="btn-primary" data-testid="save-customer-btn">
                  {editingCustomer ? "Mettre à jour" : "Créer"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="bg-white border border-slate-200 rounded-sm">
        <CardContent className="pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher par nom, email ou téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-sm"
              data-testid="search-customers"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customers table */}
      <Card className="table-container">
        <Table className="data-table">
          <TableHeader className="table-header">
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Adresse</TableHead>
              <TableHead className="text-center">Échéance</TableHead>
              <TableHead className="text-right">Total achats</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCustomers.length > 0 ? (
              filteredCustomers.map((customer) => (
                <RowMenu key={customer.id} title={`Client ${customer.name}`} copyText={customer.phone} items={[
  { label: "Modifier la fiche", onClick: () => handleEdit(customer) },
  { label: "Relevé de compte", onClick: () => navigate(`/accounts/customer/${customer.id}`) },
  { label: "Nouvelle facture pour ce client", onClick: () => navigate("/pos") },
  canDelete && { separator: true },
  canDelete && { label: "Supprimer le client", danger: true, onClick: () => handleDelete(customer.id) },
]}><TableRow key={customer.id} className="table-row">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-slate-600">
                          {customer.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="font-medium text-slate-900">{customer.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {customer.email && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Mail className="w-3 h-3" />
                          {customer.email}
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Phone className="w-3 h-3" />
                          {customer.phone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {customer.address || "-"}
                  </TableCell>
                  <TableCell className="text-center">
                    {customer.payment_terms_days != null && customer.payment_terms_days !== "" ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-mono bg-blue-100 text-blue-700" data-testid={`terms-badge-${customer.id}`}><Clock className="inline w-3 h-3 mr-1" />{customer.payment_terms_days} j</span>
                    ) : (
                      <span className="text-xs text-slate-400">Global ({settings.overdue_days || 7} j)</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(customer.total_purchases || 0)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/ledger/customer/${customer.id}`} title="Grand livre">
                        <Button variant="ghost" size="sm" className="text-blue-700" data-testid={`ledger-customer-${customer.id}`}>
                          <BookOpen className="w-4 h-4 mr-1" /> Grand livre
                        </Button>
                      </Link>
                      <AttachmentsButton entityType="customer" entityId={customer.id} entityName={customer.name} />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(customer)}
                        data-testid={`edit-customer-${customer.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                        {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(customer.id)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-customer-${customer.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        )}
                    </div>
                  </TableCell>
                </TableRow>
</RowMenu>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <div className="empty-state">
                    <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>Aucun client trouvé</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default CustomersPage;
